import classNames from 'classnames';
import type { HTMLAttributes, ReactNode } from 'react';

import { Icon } from '../Icon';
import type { IconName } from '../Icon';
import { Typography } from '../Typography';

import styles from './Banner.module.css';

export type BannerType = 'success' | 'info' | 'warning' | 'error' | 'brand';

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  type?: BannerType;
  /** Optional bold heading rendered above the body text. */
  title?: ReactNode;
  /** Overrides the default leading icon for the type. */
  icon?: ReactNode;
  /**
   * The design's "My Files Banner" (HS-1441): the icon sits in a 40 px tinted box, the corners
   * are 16 px, and the body is regular weight in the secondary text colour — only the title
   * carries the type's colour.
   */
  featured?: boolean;
  children: ReactNode;
}

const DEFAULT_ICON: Record<BannerType, IconName> = {
  success: 'check-circle',
  info: 'info-circle',
  warning: 'alert-circle',
  error: 'x-circle',
  brand: 'info-circle',
};

/**
 * Inline, soft-filled status banner. Five semantic types, each with a default leading
 * icon, an optional bold title above the body text, and a `featured` variant that boxes
 * the icon.
 *
 * @interim Ported from the reference design; replaceable by the UI Kit without API breakage.
 */
export function Banner({
  type = 'info',
  title,
  icon,
  featured = false,
  children,
  className,
  role,
  ...rest
}: BannerProps) {
  const resolvedRole = role ?? (type === 'error' || type === 'warning' ? 'alert' : 'status');

  return (
    <div
      className={classNames(styles.banner, styles[type], featured && styles.featured, className)}
      role={resolvedRole}
      {...rest}
    >
      <span className={styles.icon} aria-hidden="true">
        {icon ?? <Icon name={DEFAULT_ICON[type]} />}
      </span>
      <div className={styles.content}>
        {title != null ? (
          <Typography as="p" variant="text-sm" weight="semibold">
            {title}
          </Typography>
        ) : null}
        <Typography as="p" variant="text-sm" weight="medium">
          <span className={styles.body}>{children}</span>
        </Typography>
      </div>
    </div>
  );
}
